import {naturalSortByKey, noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useState} from 'react';

import {getActionButtonRoleProps} from 'lib/a11y/ButtonA11y';
import TextAndUrlValueEditor from 'metadata/TextAndUrlValueEditor';
import MulticolorStripedCircle from 'samples/plate-maps/MulticolorStripedCircle';
import {
  AssignmentGroup,
  AssignmentGroupValueTypeByLayer,
  BaseSchematizedAttribute,
  PlateMap,
  PlateMapWellDisplayLayer,
  SchematizedAttributeType,
} from 'samples/plate-maps/PlateMapConstants';
import {
  applySchemaTagEditToSelected,
  getColor,
  getSchematizedAttributeTagFromWell,
  getValueUrlId,
} from 'samples/plate-maps/PlateMapUtil';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import BaseEditableField from 'ui-library/BaseEditableField';
import Button, {ButtonStyle, ButtonSize} from 'ui-library/buttons/Button';
import {GRAY_DARK, GRAY_DARKER, GRAY_LIGHTER, RED_DARK} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as modals from 'util/Modals';
import {omit} from 'util/omitKey';

const NEW_OPTION_SENTINEL = 'NEW_OPTION_SENTINEL';

function TextValueOption({
  assignmentGroup,
  selectedAttribute,
  initialIsEditing,
  existingTextValues,
  deleteOption,
  saveOption,
  assignOptionValue,
  onCancelEditing,
  isPlannedValue,
}: {
  assignmentGroup: AssignmentGroup<
    AssignmentGroupValueTypeByLayer[PlateMapWellDisplayLayer.SCHEMA_FIELD_TEXT]
  >;
  selectedAttribute: BaseSchematizedAttribute<SchemaField>;
  initialIsEditing: boolean;
  existingTextValues: Set<string>;
  deleteOption: () => void;
  saveOption: (option: {text: string; url: string}) => void;
  assignOptionValue: () => void;
  onCancelEditing?: () => void;
  isPlannedValue: boolean;
}): JSX.Element {
  const [isEditing, setIsEditing] = useState<boolean>(initialIsEditing);
  const [optionValue, setOptionValue] = useState<{text: string; url: string}>(assignmentGroup.value);
  const isOptionTextDuplicate =
    optionValue.text !== assignmentGroup.value.text && existingTextValues.has(optionValue.text);

  function onOptionClick(): void {
    if (!isEditing) {
      assignOptionValue();
    }
  }

  function onClickCancel(): void {
    if (onCancelEditing == null) {
      setOptionValue(assignmentGroup.value);
      setIsEditing(false);
    } else {
      onCancelEditing();
    }
  }

  function onSaveChanges(): void {
    if (!isOptionTextDuplicate && optionValue.text.length > 0) {
      saveOption(optionValue);
      setIsEditing(false);
    }
  }

  return (
    <div
      className={css(styles.textValueOption)}
      onClick={onOptionClick}
      {...getTestProps({
        component: 'TextFieldAssignmentSettings',
        element: 'option',
        key: assignmentGroup.id,
      })}
    >
      <div className={css(styles.textOption)}>
        <div className={css(styles.colorCircle)}>
          <MulticolorStripedCircle cx={6} cy={6} radius={6} colors={[assignmentGroup.color]} />
        </div>
        <BaseEditableField
          editor={
            <TextAndUrlValueEditor
              autoFocus={true}
              defaultValue={{
                value: optionValue.text,
                url: optionValue.url,
                tagSchemaField: selectedAttribute.schemaField,
                name: selectedAttribute.schemaField.name,
              }}
              itemLinkAllowed={selectedAttribute.type === SchematizedAttributeType.WELL_SCHEMA_FIELD}
              onChange={(newValue: {value: string; url: string}) => {
                setOptionValue({text: newValue.value, url: newValue.url});
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onSaveChanges();
                }
              }}
              placeholder="Label"
            />
          }
          extraActionButtons={{
            viewMode: [
              <BenchlingIcon
                key="delete"
                aria-label="Delete"
                className={css(styles.deleteIcon)}
                icon="delete"
                {...getActionButtonRoleProps({
                  onPress: () => {
                    deleteOption();
                  },
                  stopPropagation: true,
                })}
              />,
            ],
            editMode: [],
          }}
          isBeingEdited={isEditing}
          isCompleteEnabled={!isOptionTextDuplicate && optionValue.text.length > 0}
          isWritable={!isPlannedValue}
          viewer={
            <Tooltip
              text="Planned value labels cannot be edited or removed"
              isDisabled={!isPlannedValue}
              key="delete"
            >
              <div className={css(styles.optionName)}>
                {assignmentGroup.value.url.length > 0 ? (
                  <a href={assignmentGroup.value.url} target="_blank" rel="noreferrer">
                    {assignmentGroup.value.text}
                  </a>
                ) : (
                  <span>{assignmentGroup.value.text}</span>
                )}
              </div>
            </Tooltip>
          }
          onCancel={onClickCancel}
          onComplete={onSaveChanges}
          onEdit={(e) => {
            e.stopPropagation();
            setIsEditing(true);
          }}
          isFullWidth={true}
        />
      </div>
      {isOptionTextDuplicate && (
        <p
          className={css(styles.errorMessage)}
          {...getTestProps({component: 'TextFieldAssignmentSettings', element: 'duplicateLabelError'})}
        >{`Label ${optionValue.text} already exists.`}</p>
      )}
    </div>
  );
}

export interface TextFieldAssignmentSettingsProps {
  plateMap: PlateMap;
  onEditPlateMap: (plateMap: PlateMap) => void;
  selectedWellIndices: Set<number>;
  deselectWells: () => void;
  selectedAttribute: BaseSchematizedAttribute<SchemaField>;
  assignmentGroupsById: {
    [id: string]: AssignmentGroup<
      AssignmentGroupValueTypeByLayer[PlateMapWellDisplayLayer.SCHEMA_FIELD_TEXT]
    >;
  };
  setAssignmentGroupsById: (groups: {
    [id: string]: AssignmentGroup<
      AssignmentGroupValueTypeByLayer[PlateMapWellDisplayLayer.SCHEMA_FIELD_TEXT]
    >;
  }) => void;
}

/**
 * Settings to assign text schema field values for the given schema field to plate cells.
 *
 * @param plateMap The current state of the plate and its well attributes
 * @param onEditPlateMap Function to update with a new plate map
 * @param selectedWellIndices Set of wells that are currently selected
 * @param deselectWells Function to deselect all wells
 * @param selectedAttribute The schematized attribute of type text whose values are being edited
 * @param assignmentGroupsById Assignment groups for the selected schema field
 * @param setAssignmentGroupsById Function to update with new assignment groups
 */
export default function TextFieldAssignmentSettings({
  plateMap,
  onEditPlateMap,
  selectedWellIndices,
  deselectWells,
  selectedAttribute,
  assignmentGroupsById,
  setAssignmentGroupsById,
}: TextFieldAssignmentSettingsProps): JSX.Element {
  const [addingNewOption, setAddingNewOption] = useState<boolean>(false);
  const [nextColor, setNextColor] = useState<string>(getColor(assignmentGroupsById).next().value);
  const [existingTextValues, setExistingTextValues] = useState<Set<string>>(new Set());

  const plannedValueGroupIds = new Set(
    selectedAttribute.plannedValues.map((pv) => getValueUrlId(pv.value, pv.url))
  );

  useEffect(() => {
    setNextColor(getColor(assignmentGroupsById).next().value);
    const textValues = Object.values(assignmentGroupsById).map(({value}) => value.text);
    setExistingTextValues(new Set(textValues));
  }, [assignmentGroupsById]);

  function getWellIndicesWithValue(value: {text: string; url: string}): Set<number> {
    const indices: Set<number> = new Set();
    plateMap.wells.forEach((well, index) => {
      const tagValue = getSchematizedAttributeTagFromWell(well, selectedAttribute);
      if (tagValue?.value === value.text && (tagValue?.url ?? '') === value.url) {
        indices.add(index);
      }
    });
    return indices;
  }

  async function deleteGroup(
    group: AssignmentGroup<AssignmentGroupValueTypeByLayer[PlateMapWellDisplayLayer.SCHEMA_FIELD_TEXT]>
  ): Promise<void> {
    const wellIndices = getWellIndicesWithValue(group.value);
    if (wellIndices.size > 0) {
      const confirmed = await modals.confirm(
        `Remove label ${group.name}`,
        `This label will be removed from ${wellIndices.size} wells. Are you sure you want to continue?`,
        ButtonStyle.NEGATIVE,
        {acceptText: 'Remove', rejectText: 'Cancel', enableRedesign: true, promiseCompatible: true}
      );
      if (!confirmed) {
        return;
      }
      const updatedPlateMap = applySchemaTagEditToSelected(
        plateMap,
        selectedAttribute,
        {value: null, url: null},
        wellIndices
      );
      onEditPlateMap(updatedPlateMap);
    }
    setAssignmentGroupsById(omit(assignmentGroupsById, group.id));
  }

  function saveOptionChanges(
    group: AssignmentGroup<AssignmentGroupValueTypeByLayer[PlateMapWellDisplayLayer.SCHEMA_FIELD_TEXT]>,
    newValue: {text: string; url: string}
  ): void {
    const newGroupId = getValueUrlId(newValue.text, newValue.url)!;
    setAssignmentGroupsById({
      ...omit(assignmentGroupsById, group.id),
      [newGroupId]: {
        id: newGroupId,
        color: group.color,
        name: newValue.text,
        value: newValue,
      },
    });

    const wellIndices = getWellIndicesWithValue(group.value);
    if (wellIndices.size > 0) {
      const updatedPlateMap = applySchemaTagEditToSelected(
        plateMap,
        selectedAttribute,
        {value: newValue.text, url: newValue.url},
        wellIndices
      );
      onEditPlateMap(updatedPlateMap);
    }
  }

  function assignValueToSelected(newValue: {text: string; url: string}): void {
    if (selectedWellIndices.size === 0) {
      return;
    }
    const updatedPlateMap = applySchemaTagEditToSelected(
      plateMap,
      selectedAttribute,
      {value: newValue.text, url: newValue.url},
      selectedWellIndices
    );
    onEditPlateMap(updatedPlateMap);
    deselectWells();
  }

  function addNewOption(option: {text: string; url: string}): void {
    const newOptionId = getValueUrlId(option.text, option.url)!;
    setAssignmentGroupsById({
      ...assignmentGroupsById,
      [newOptionId]: {
        id: newOptionId,
        color: nextColor,
        name: option.text,
        value: option,
      },
    });
    assignValueToSelected(option);
    setAddingNewOption(false);
  }

  // Sort alphabetically with planned values first
  const sortedAssignmentGroups = naturalSortByKey(Object.values(assignmentGroupsById), 'name').sort(
    (a, b) => {
      const aPlanned = plannedValueGroupIds.has(a.id);
      const bPlanned = plannedValueGroupIds.has(b.id);
      return Number(bPlanned) - Number(aPlanned);
    }
  );

  return (
    <div {...getTestProps({component: 'TextFieldAssignmentSettings', element: 'root'})}>
      <p className={css(styles.helpText)}>Select wells in the plate, then select a label to apply to them</p>
      <div className={classNames(css(styles.optionList), 'scrollbar-override')}>
        {sortedAssignmentGroups.map((group) => (
          <TextValueOption
            key={group.id}
            assignmentGroup={group}
            selectedAttribute={selectedAttribute}
            initialIsEditing={false}
            deleteOption={() => {
              deleteGroup(group);
            }}
            saveOption={(option) => {
              saveOptionChanges(group, option);
            }}
            assignOptionValue={() => {
              assignValueToSelected(group.value);
            }}
            existingTextValues={existingTextValues}
            isPlannedValue={plannedValueGroupIds.has(group.id)}
          />
        ))}
        {addingNewOption ? (
          <TextValueOption
            key={NEW_OPTION_SENTINEL}
            assignmentGroup={{
              id: NEW_OPTION_SENTINEL,
              color: nextColor,
              name: NEW_OPTION_SENTINEL,
              value: {text: '', url: ''},
            }}
            selectedAttribute={selectedAttribute}
            initialIsEditing={true}
            deleteOption={noop}
            saveOption={addNewOption}
            assignOptionValue={noop}
            onCancelEditing={() => {
              setAddingNewOption(false);
            }}
            existingTextValues={existingTextValues}
            isPlannedValue={false}
          />
        ) : (
          <Button
            buttonStyle={ButtonStyle.TEXT}
            size={ButtonSize.SMALL}
            icon="compose"
            text="Add label"
            disabled={false}
            onClick={() => {
              setAddingNewOption(true);
            }}
          />
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  helpText: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
  },
  optionList: {
    marginTop: '6px',
    overflowY: 'auto',
  },
  textValueOption: {
    cursor: 'pointer',
    width: '100%',
    padding: '4px 4px',
    ':hover': {
      backgroundColor: GRAY_LIGHTER,
    },
  },
  textOption: {
    display: 'flex',
    alignItems: 'center',
  },
  colorCircle: {
    marginRight: '4px',
    display: 'inline-flex',
  },
  optionName: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  deleteIcon: {
    color: GRAY_DARK,
  },
  errorMessage: {
    color: RED_DARK,
    margin: '4px 0 0 16px',
  },
});
